import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { Layer, ViewLarge, Viewport, ViewSmall } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { QueryRoute, RouteStore } from 'bernie-core';

import { AmenityViewModel, HighlightAmenitiesViewModel, PropertyInfoSectionViewModel } from 'models';

import { classNames } from 'uitk-react-utils';

import { AmenitiesCarousel } from './internals/amenities-carousel';
import { AmenitiesDialog } from 'components/infosite/property-details/amenities/internals/amenities-dialog';
import { AmenitiesGrid } from './internals/amenities-grid';
import { AmenitiesLink } from './internals/amenities-link';

const amenitiesClass = 'amenities__list uitk-cell';
const iconId = 'amenities_view_all';
const linkName = 'View all amenities';

export interface AmenitiesProps {
  carouselAmenities?: AmenityViewModel[];
  highlightAmenities?: HighlightAmenitiesViewModel[];
  propertyAmenities?: PropertyInfoSectionViewModel;
  routeStore?: RouteStore;
  isRefreshedHISLayout?: boolean;
}

export interface AmenitiesState {
  initialized: boolean;
}

type LocalizedAmenitiesProps = LocalizedComponentProps & AmenitiesProps;

@withLocalization
@inject('routeStore', 'isRefreshedHISLayout')
@observer
export class Amenities extends React.Component<LocalizedAmenitiesProps, AmenitiesState> {
  private amenitiesWrapperRef: HTMLElement = undefined;
  private amenitiesDialogRef: HTMLElement = undefined;

  constructor(props) {
    super(props);
    this.state = {
      initialized: false
    };

    this.onAmenitiesDialogClosed = this.onAmenitiesDialogClosed.bind(this);
  }

  public componentDidMount() {
    this.setState({
      initialized: true
    });
  }

  public render() {
    const { initialized } = this.state;
    const { carouselAmenities, highlightAmenities, l10n, propertyAmenities, routeStore, isRefreshedHISLayout } = this.props;

    const amenitiesWrapperRef = wrapperRef => {
      this.amenitiesWrapperRef = wrapperRef;
    };

    const amenitiesDialogRef = dialogRef => {
      this.amenitiesDialogRef = dialogRef;
    };

    const renderAmenities = carouselAmenities && carouselAmenities.length > 0;
    const linkText = l10n.formatText('hotels.infosite.amenities.viewAll.title');
    const linkProps = { amenitiesDialogRef, iconId, linkName, linkText, routeStore };
    const linkClass = `uitk-type-300 ${isRefreshedHISLayout ? '' : ' all-cell-align-end'}`;
    const linkWrapperClass = classNames('uitk-cell', {
      'uitk-type-right all-t-margin-one': !isRefreshedHISLayout,
      'uitk-type-left all-t-margin-three': isRefreshedHISLayout
    });
    const amenitiesCarousel = (
      <div className={`${amenitiesClass} all-b-padding-two`}>
        {renderAmenities && <AmenitiesCarousel amenities={carouselAmenities} />}
        {initialized && (
          <div className={`${linkClass} amenities-view-all`}>
            <AmenitiesLink {...linkProps} />
          </div>
        )}
      </div>
    );
    const amenitiesGrid = (
      <React.Fragment>
        <div className={amenitiesClass}>
          {renderAmenities && <AmenitiesGrid amenities={carouselAmenities} moreAmenities={isRefreshedHISLayout} />}
        </div>
        {initialized && (
          <div className={linkWrapperClass}>
            <AmenitiesLink {...linkProps} linkClass={linkClass} />
          </div>
        )}
      </React.Fragment>
    );

    return (
      <div className="amenities uitk-grid" data-stid="hotel-amenities-list" ref={amenitiesWrapperRef}>
        <Viewport>
          <ViewSmall>{amenitiesCarousel}</ViewSmall>
          <ViewLarge>{amenitiesGrid}</ViewLarge>
        </Viewport>
        <QueryRoute query={{ pwaDialog: 'amenities' }}>
          <Layer id="infosite-amenities" onDismiss={this.onAmenitiesDialogClosed}>
            <AmenitiesDialog
              highlightAmenities={highlightAmenities}
              propertyAmenities={propertyAmenities}
              onDismiss={this.onAmenitiesDialogClosed}
            />
          </Layer>
        </QueryRoute>
      </div>
    );
  }

  public onAmenitiesDialogClosed() {
    // Close the amenities overlay
    this.props.routeStore.goBack();

    this.focusAmenitiesRef();
  }

  private focusAmenitiesRef() {
    if (this.amenitiesDialogRef && typeof this.amenitiesDialogRef.focus === 'function') {
      this.amenitiesDialogRef.focus();
    } else if (this.amenitiesWrapperRef && typeof this.amenitiesWrapperRef.focus === 'function') {
      this.amenitiesWrapperRef.focus();
    }
  }
}
